import {
  OrganizationType,
  type PersonInfluencerResponse,
  type PlayerProfileResponse,
  rdbV1NilRankingsList,
  rdbV1PeopleLatestValuationDetail,
  rdbV1PeopleSocialDetail,
  rdbV1PeopleValuationGrowthDetail,
  rdbV2Nil100List,
  v1PeopleDealsDetail,
} from '@on3/api';
import { type IGenericPageProps } from '@on3/ui-lib/api/schema/custom-contracts';
import { withAuthentication } from '@on3/ui-lib/src/hocs/withAuthentication';
import { withValidUrl } from '@on3/ui-lib/src/hocs/withValidUrl';
import {
  getFulfilledValue,
  proxyApi,
  proxyApiWithVary,
} from '@on3/ui-lib/utils/api';
import { slugify } from '@on3/ui-lib/utils/slugify';
import { getIdFromParam, getSportKeyFromParam } from '@on3/ui-lib/utils/url';
import { AthleteClaimModal } from 'components/Db/Profile/AthleteClaim/AthleteClaimModal';
import {
  PlayerNIL,
  type PlayerNilPageProps,
} from 'components/Db/Profile/Nil/PlayerNIL';
import { PageWrapper } from 'components/Layouts/PageWrapper';
import { AthleteProvider } from 'contexts/AthleteProvider';
import type { GetServerSideProps } from 'next';
import { useMemo } from 'react';
import { getSportKeyFromPage, playerModules } from 'utils/player';

interface SocialLinks {
  type: string;
  handle: string;
}

export interface IPlayerNILPageProps extends PlayerNilPageProps {
  player: PlayerProfileResponse;
  social?: SocialLinks[];
  socialData?: PersonInfluencerResponse[];
}

interface IPlayerNILPage extends IPlayerNILPageProps, IGenericPageProps {}

const PlayerNILPage = ({
  player,
  latestValuation,
  valuationGrowth,
  playerDeals,
  socialData,
  nil100,
  nilSport,
  nilTeam,
  ...props
}: IPlayerNILPage) => {
  const data = useMemo(() => {
    return { athleteData: player || undefined, socialData };
  }, [player, socialData]);

  const title = `${player?.name} - NIL Profile`;
  const description = `Latest NIL valuation and social data for ${player?.name}`;
  const defaultAsset = player?.defaultAsset;

  const image = useMemo(() => {
    const defaultImage = { height: 200, src: '', width: 200 };

    if (!defaultAsset) {
      return defaultImage;
    }

    const { domain, source, height, width } = defaultAsset;
    const src = (domain && source && `https://${domain}${source}`) || '';

    return { height: height || 200, src, width: width || 200 };
  }, [defaultAsset]);

  const noIndex = !latestValuation?.valuation;

  return (
    <AthleteProvider data={data}>
      <AthleteClaimModal />
      <PageWrapper
        {...props}
        description={description}
        image={image}
        noIndex={noIndex}
        ogType="athlete"
        title={title}
      >
        <PlayerNIL
          latestValuation={latestValuation}
          nil100={nil100}
          nilSport={nilSport}
          nilTeam={nilTeam}
          playerDeals={playerDeals}
          valuationGrowth={valuationGrowth}
        />
      </PageWrapper>
    </AthleteProvider>
  );
};

export const getServerSideProps: GetServerSideProps = async (ctx) => {
  const siteData = await withValidUrl.isValid(ctx);
  const userData = await withAuthentication.isAuth(ctx);
  const { requestWithVaryParams } = userData || {};
  const { id, sport } = ctx.query as {
    id: string;
    sport?: string;
  };
  const personKey = getIdFromParam(id);
  let sportKey = getSportKeyFromParam(slugify(sport)) ?? undefined;

  if (!personKey || !siteData) {
    return {
      notFound: true,
    };
  }

  try {
    const player = await playerModules.player(
      proxyApi,
      { personKey, sportKey },
      userData?.requestParams,
    );

    if (!player || (!player.isAthlete && player.isCoach)) {
      return {
        notFound: true,
      };
    }

    if (id !== player.slug) {
      return {
        redirect: {
          permanent: true,
          destination: `/rivals/${player.slug}/nil/`,
        },
      };
    }

    sportKey = getSportKeyFromPage(player, sport);

    if (![1, 2, 5, 12].includes(sportKey)) {
      return {
        redirect: {
          destination: `/rivals/${player.slug}/`,
          permanent: true,
        },
      };
    }

    const data = {
      pageType: 'database',
      siteData,
      userData: userData || [],
      player,
    };

    type Promises = {
      [K in keyof IPlayerNILPageProps]: () => Promise<IPlayerNILPageProps[K]>;
    };

    const promises = {
      latestValuation: () =>
        rdbV1PeopleLatestValuationDetail(
          proxyApiWithVary,
          personKey,
          requestWithVaryParams,
        ),
      valuationGrowth: () =>
        rdbV1PeopleValuationGrowthDetail(proxyApi, { personKey }),
      playerDeals: () => v1PeopleDealsDetail(proxyApi, { personKey }),
      socialData: () => rdbV1PeopleSocialDetail(proxyApi, personKey),
      nil100: () => rdbV2Nil100List(proxyApi, { limit: 5 }),
    } as Omit<Promises, 'player'>;

    if (player.organizationLevel === OrganizationType.HighSchool) {
      promises.nilSport = () =>
        rdbV1NilRankingsList(
          proxyApiWithVary,
          {
            orgType: OrganizationType.HighSchool,
            sportKey,
          },
          requestWithVaryParams,
        );
    }

    if (player.organizationLevel === OrganizationType.College) {
      promises.nilTeam = () =>
        rdbV2Nil100List(proxyApi, {
          orgKey: player?.collegeOrgKey || 0,
          limit: 5,
        });
    }

    // Fetch all player data in parallel here
    const resolvedPromises = await Promise.allSettled(
      Object.values(promises).map((fn) => fn()),
    );

    resolvedPromises.forEach((promise, i) => {
      const key = Object.keys(promises)[i] as keyof Promises;

      Object.assign(data, { [key]: getFulfilledValue(promise) });
    });

    return { props: data };
  } catch (e) {
    return { props: {} };
  }
};

export default withValidUrl(PlayerNILPage);
